import type { FC } from 'react';

import { Button, Progress, Stack, Text, cn } from '@eluve/components';

import type { ReviewProgress } from './useFindingsReview';

export const ReviewProgressHeader: FC<{
  progress: ReviewProgress;
  allReviewed: boolean;
  rerunning?: boolean;
  busy?: boolean;
  showApply?: boolean;
  onApply: () => void;
  onRerun: () => void;
}> = ({
  progress,
  allReviewed,
  rerunning,
  busy,
  showApply = true,
  onApply,
  onRerun,
}) => {
  const pending = progress.total - progress.reviewed;
  const pct = progress.total
    ? Math.round((progress.reviewed / progress.total) * 100)
    : 0;

  const tallies = [
    { count: progress.approved, label: 'approved', tone: 'text-text-positive' },
    { count: progress.rejected, label: 'rejected', tone: 'text-text-negative' },
    { count: pending, label: 'need review', tone: 'text-text-secondary' },
  ];

  return (
    <div className="w-full rounded-xl border border-border-secondary bg-bg-primary px-5 py-4">
      <Stack direction="horizontal" align="start" justify="between" gap={4}>
        <Stack gap={2} className="min-w-0 flex-1">
          <Stack gap={1}>
            <Text size="md" weight="semibold" className="tabular-nums">
              {progress.reviewed} of {progress.total} findings reviewed
            </Text>
            <Text size="sm" color="tertiary">
              Review these findings to help Eluve generate an accurate care
              plan.
            </Text>
          </Stack>

          <Progress
            value={pct}
            className="h-2 bg-bg-tertiary"
            indicatorClassName="bg-bg-positive-strong-primary"
          />

          <Stack direction="horizontal" align="center" gap={4}>
            {tallies.map(({ count, label, tone }) => (
              <Text key={label} size="xs" color="tertiary">
                <Text
                  inline
                  size="xs"
                  weight="semibold"
                  className={cn(tone, 'tabular-nums')}
                >
                  {count}
                </Text>{' '}
                {label}
              </Text>
            ))}
            <span className="flex-1" />
            <Button size="sm" variant="ghost" disabled={busy} onClick={onRerun}>
              {rerunning ? 'Re-running…' : 'Re-run extraction'}
            </Button>
          </Stack>
        </Stack>

        {showApply ? (
          <Button
            size="sm"
            className="shrink-0"
            disabled={busy || !allReviewed}
            onClick={onApply}
          >
            Apply to care plan
          </Button>
        ) : null}
      </Stack>
    </div>
  );
};
